import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
  Nav,
  Tab,
} from "react-bootstrap";
import {
  FaSave,
  FaTimes,
  FaBuilding,
  FaUser,
  FaGlobe,
  FaMapMarkerAlt,
  FaBriefcase,
  FaCheck,
} from "react-icons/fa";
import {
  createCompany,
  updateCompany,
} from "../../../features/companies/companiesSlice";
import {
  CompanyCreate,
  CompanyUpdate,
  Company,
} from "../../../features/companies/companiesService";
import { AppDispatch, RootState } from "../../../store";
import ErrorAlert from "../../../components/common/ErrorAlert";
import { Icon } from "../../../components/common/Icon";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { getCompanyTypeOptions } from "../../../utils/permissions";

interface CompanyFormModalProps {
  show: boolean;
  onHide: () => void;
  company?: Company;
  initialCompanyType?: string;
}

// İlk form verilerini oluşturan fonksiyon
const getInitialFormData = (companyType: string = "client"): CompanyCreate => {
  return {
    company_name: "",
    company_type: companyType,
    industry: "",
    establishment_date: "",
    description: "",
    website: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    country: "",
    postal_code: "",
    tax_id: "",
    company_size: "",
    annual_revenue: undefined,
    logo_url: "",
    is_active: true,
  };
};

const CompanyFormModal: React.FC<CompanyFormModalProps> = ({
  show,
  onHide,
  company,
  initialCompanyType,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { isLoading, error } = useSelector(
    (state: RootState) => state.companies
  );
  const { user } = useSelector((state: RootState) => state.auth);

  const [formData, setFormData] = useState<CompanyCreate>(
    getInitialFormData(company?.company_type || "client")
  );
  const [validated, setValidated] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  // Şirket düzenleme modunda formu doldur veya initialCompanyType değiştiğinde güncelle
  useEffect(() => {
    if (company) {
      setFormData({
        company_name: company.company_name || "",
        company_type: company.company_type || "client",
        industry: company.industry || "",
        establishment_date: company.establishment_date || "",
        description: company.description || "",
        website: company.website || "",
        email: company.email || "",
        phone: company.phone || "",
        address: company.address || "",
        city: company.city || "",
        country: company.country || "",
        postal_code: company.postal_code || "",
        tax_id: company.tax_id || "",
        company_size: company.company_size || "",
        annual_revenue: company.annual_revenue,
        logo_url: company.logo_url || "",
        is_active: company.is_active,
      });
    } else {
      setFormData(getInitialFormData(initialCompanyType || "client"));
    }
    setValidated(false);
    setActiveTab("basic");
  }, [company, show, initialCompanyType]);

  const isEditing = Boolean(company);

  // Form alanlarını güncelle
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const checkboxInput = e.target as HTMLInputElement;
      setFormData((prev) => ({ ...prev, [name]: checkboxInput.checked }));
    } else if (name === "annual_revenue") {
      setFormData((prev) => ({
        ...prev,
        [name]: value ? Number(value) : undefined,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Boş stringleri göndermemek için form verilerini temizle
  const buildCleanCompanyCreate = (data: CompanyCreate): CompanyCreate => {
    const requiredKeys: (keyof CompanyCreate)[] = ["company_name", "company_type"]; // is_active optional
    const clean: Partial<CompanyCreate> = {};
    (Object.keys(data) as (keyof CompanyCreate)[]).forEach((key) => {
      const value = data[key];
      if (requiredKeys.includes(key)) {
        clean[key] = value as any;
        return;
      }
      if (value === undefined || value === null) return;
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed.length === 0) return;
        if (key === "establishment_date" && trimmed === "") return;
        clean[key] = trimmed as any;
        return;
      }
      clean[key] = value as any;
    });
    return clean as CompanyCreate;
  };

  // Formu gönder
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      // Hatalı alana sahip sekmeye git
      if (!formData.company_name) {
        setActiveTab("basic");
      }
      return;
    }

    setValidated(true);

    try {
      if (isEditing && company) {
        await dispatch(
          updateCompany({
            companyId: company.id,
            companyData: buildCleanCompanyCreate(formData) as CompanyUpdate,
          })
        ).unwrap();
      } else {
        await dispatch(createCompany(buildCleanCompanyCreate(formData) as CompanyCreate)).unwrap();
      }
      onHide();
    } catch (error) {
      // Error is handled by the reducer
    }
  };

  // Sekme tamamlanma durumunu kontrol et
  const isTabCompleted = (tabKey: string) => {
    switch (tabKey) {
      case "basic":
        return formData.company_name.trim() !== "";
      case "contact":
        return formData.email || formData.phone || formData.website;
      case "address":
        return formData.address || formData.city || formData.country;
      case "business":
        return formData.tax_id || formData.company_size;
      default:
        return false;
    }
  };

  // Sekme ikonları
  const getTabIcon = (tabKey: string) => {
    switch (tabKey) {
      case "basic":
        return FaBuilding;
      case "contact":
        return FaGlobe;
      case "address":
        return FaMapMarkerAlt;
      case "business":
        return FaBriefcase;
      default:
        return FaBuilding;
    }
  };

  // Başlığı belirle
  const modalTitle = isEditing ? t("edit_company") : t("add_new_company");
  const modalIcon = isEditing ? FaBuilding : FaSave;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      backdrop="static"
      keyboard={false}
      className="modern-modal"
      scrollable
    >
      <Modal.Header className="border-0 pb-0 px-4 pt-4 modal-header-custom">
        <div className="d-flex align-items-center w-100">
          <div className="modal-icon-wrapper me-3">
            <Icon icon={modalIcon} className="modal-icon" size="1.5rem" />
          </div>
          <div className="flex-grow-1">
            <Modal.Title className="mb-0 fw-bold h4 modal-title-custom">
              {modalTitle}
            </Modal.Title>
            <p className="modal-subtitle mb-0 small">
              {isEditing
                ? t("update_company_information")
                : t("enter_company_details")}
            </p>
          </div>
          <Button
            variant="outline-secondary"
            onClick={onHide}
            className="close-btn-custom"
          >
            <Icon icon={FaTimes} />
          </Button>
        </div>
      </Modal.Header>

      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Modal.Body className="px-4 py-4 modal-body-custom">
          {error && (
            <Alert variant="danger" className="error-alert-custom mb-4">
              <strong>{t("error")}:</strong> {error}
            </Alert>
          )}

          {/* Tab Navigation */}
          <Tab.Container
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || "basic")}
          >
            <Nav variant="pills" className="nav-tabs-modern mb-4">
              <Nav.Item>
                <Nav.Link
                  eventKey="basic"
                  className="d-flex align-items-center nav-link-custom"
                >
                  <div className="tab-icon-wrapper me-2">
                    <Icon icon={getTabIcon("basic")} />
                    {isTabCompleted("basic") && (
                      <Icon icon={FaCheck} className="tab-check-icon" />
                    )}
                  </div>
                  <div>
                    <div className="tab-title">{t("basic_information")}</div>
                    <small className="tab-subtitle">
                      {t("company_details")}
                    </small>
                  </div>
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  eventKey="contact"
                  className="d-flex align-items-center nav-link-custom"
                >
                  <div className="tab-icon-wrapper me-2">
                    <Icon icon={getTabIcon("contact")} />
                    {isTabCompleted("contact") && (
                      <Icon icon={FaCheck} className="tab-check-icon" />
                    )}
                  </div>
                  <div>
                    <div className="tab-title">{t("contact_information")}</div>
                    <small className="tab-subtitle">
                      {t("communication_details")}
                    </small>
                  </div>
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  eventKey="address"
                  className="d-flex align-items-center nav-link-custom"
                >
                  <div className="tab-icon-wrapper me-2">
                    <Icon icon={getTabIcon("address")} />
                    {isTabCompleted("address") && (
                      <Icon icon={FaCheck} className="tab-check-icon" />
                    )}
                  </div>
                  <div>
                    <div className="tab-title">{t("address_information")}</div>
                    <small className="tab-subtitle">
                      {t("location_details")}
                    </small>
                  </div>
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  eventKey="business"
                  className="d-flex align-items-center nav-link-custom"
                >
                  <div className="tab-icon-wrapper me-2">
                    <Icon icon={getTabIcon("business")} />
                    {isTabCompleted("business") && (
                      <Icon icon={FaCheck} className="tab-check-icon" />
                    )}
                  </div>
                  <div>
                    <div className="tab-title">{t("business_information")}</div>
                    <small className="tab-subtitle">
                      {t("financial_details")}
                    </small>
                  </div>
                </Nav.Link>
              </Nav.Item>
            </Nav>

            {/* Tab Content */}
            <Tab.Content>
              {/* Basic Information Tab */}
              <Tab.Pane eventKey="basic">
                <div className="tab-content-wrapper">
                  <Row className="g-3">
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label className="form-label-custom mb-2">
                          {t("company_name")}{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          required
                          type="text"
                          name="company_name"
                          value={formData.company_name}
                          onChange={handleChange}
                          placeholder={t("enter_company_name")}
                          className="form-control-modern"
                        />
                        <Form.Control.Feedback type="invalid">
                          {t("please_enter_company_name")}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="form-label-custom mb-2">
                          {t("company_type")}{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          required
                          name="company_type"
                          value={formData.company_type}
                          onChange={handleChange}
                          className="form-control-modern"
                          disabled={(!!initialCompanyType && !isEditing) || (user?.roles?.includes("agency_owner") && !isEditing)}
                        >
                          {(() => {
                            const options = getCompanyTypeOptions(user);
                            const values = new Set(options.map(o => o.value));
                            const ensure: { value: string; label: string }[] = [];
                            const currentValue = formData.company_type;
                            if (currentValue && !values.has(currentValue)) {
                              let label = "Client";
                              if (currentValue === "agency") label = "Agency";
                              if (currentValue === "single") label = "Single";
                              ensure.push({ value: currentValue, label });
                            }
                            return [...ensure, ...options].map((option) => (
                              <option key={option.value} value={option.value}>
                                {t(option.label.toLowerCase())}
                              </option>
                            ));
                          })()}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {t("please_select_company_type")}
                        </Form.Control.Feedback>
                        {user?.roles?.includes("agency_owner") && !isEditing && (
                          <Form.Text className="text-muted">
                            Client companies are automatically linked to your agency
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3 mt-1">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="form-label-custom mb-2">
                          {t("industry")}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="industry"
                          value={formData.industry}
                          onChange={handleChange}
                          placeholder={t("enter_industry")}
                          className="form-control-modern"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="form-label-custom mb-2">
                          {t("establishment_date")}
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="establishment_date"
                          value={formData.establishment_date}
                          onChange={handleChange}
                          className="form-control-modern"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3 mt-1">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="form-label-custom mb-2">
                          {t("description")}
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          placeholder={t("enter_company_description")}
                          className="form-control-modern"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Tab.Pane>

              {/* Contact Information Tab */}
              <Tab.Pane eventKey="contact">
                <div className="tab-content-wrapper">
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="form-label-custom mb-2">
                          {t("website")}
                        </Form.Label>
                        <Form.Control
                          type="url"
                          name="website"
                          value={formData.website}
                          onChange={handleChange}
                          placeholder="https://example.com"
                          className="form-control-modern"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="form-label-custom mb-2">
                          {t("logo_url")}
                        </Form.Label>
                        <Form.Control
                          type="url"
                          name="logo_url"
                          value={formData.logo_url}
                          onChange={handleChange}
                          placeholder="https://example.com/logo.png"
                          className="form-control-modern"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3 mt-1">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="form-label-custom mb-2">
                          {t("email")}
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="contact@company.com"
                          className="form-control-modern"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="form-label-custom mb-2">
                          {t("phone")}
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+90 555 123 45 67"
                          className="form-control-modern"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Tab.Pane>

              {/* Address Information Tab */}
              <Tab.Pane eventKey="address">
                <div className="tab-content-wrapper">
                  <Row className="g-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="form-label-custom mb-2">
                          {t("address")}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          placeholder={t("enter_full_address")}
                          className="form-control-modern"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3 mt-1">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="form-label-custom mb-2">
                          {t("city")}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          placeholder={t("enter_city")}
                          className="form-control-modern"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="form-label-custom mb-2">
                          {t("country")}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          placeholder={t("enter_country")}
                          className="form-control-modern"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="form-label-custom mb-2">
                          {t("postal_code")}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="postal_code"
                          value={formData.postal_code}
                          onChange={handleChange}
                          placeholder={t("enter_postal_code")}
                          className="form-control-modern"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Tab.Pane>

              {/* Business Information Tab */}
              <Tab.Pane eventKey="business">
                <div className="tab-content-wrapper">
                  <Row className="g-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="form-label-custom mb-2">
                          {t("tax_id")}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="tax_id"
                          value={formData.tax_id}
                          onChange={handleChange}
                          placeholder={t("enter_tax_id")}
                          className="form-control-modern"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="form-label-custom mb-2">
                          {t("company_size")}
                        </Form.Label>
                        <Form.Select
                          name="company_size"
                          value={formData.company_size}
                          onChange={handleChange}
                          className="form-control-modern"
                        >
                          <option value="">{t("select_company_size")}</option>
                          <option value="1-10">1-10 {t("employees")}</option>
                          <option value="11-50">11-50 {t("employees")}</option>
                          <option value="51-200">
                            51-200 {t("employees")}
                          </option>
                          <option value="201-500">
                            201-500 {t("employees")}
                          </option>
                          <option value="501+">501+ {t("employees")}</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="form-label-custom mb-2">
                          {t("annual_revenue")}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="annual_revenue"
                          value={formData.annual_revenue || ""}
                          onChange={handleChange}
                          placeholder={t("enter_annual_revenue")}
                          className="form-control-modern"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3 mt-3">
                    <Col md={12}>
                      <div className="status-card-custom">
                        <Form.Group className="d-flex align-items-center justify-content-between">
                          <div>
                            <Form.Label className="form-label-custom mb-1">
                              {t("active_status")}
                            </Form.Label>
                            <div className="status-description">
                              {formData.is_active
                                ? t("company_is_active")
                                : t("company_is_inactive")}
                            </div>
                          </div>
                          <Form.Check
                            type="switch"
                            id="is_active"
                            name="is_active"
                            checked={formData.is_active}
                            onChange={handleChange}
                            className="switch-custom fs-5"
                          />
                        </Form.Group>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Modal.Body>

        <Modal.Footer className="border-0 px-4 pb-4 modal-footer-custom">
          <div className="d-flex justify-content-between align-items-center w-100">
            {/* Tab Navigation Buttons */}
            <div className="d-flex gap-2">
              {activeTab !== "basic" && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    const tabs = ["basic", "contact", "address", "business"];
                    const currentIndex = tabs.indexOf(activeTab);
                    if (currentIndex > 0) {
                      setActiveTab(tabs[currentIndex - 1]);
                    }
                  }}
                  className="nav-btn-custom"
                >
                  {t("previous")}
                </Button>
              )}
              {activeTab !== "business" && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    const tabs = ["basic", "contact", "address", "business"];
                    const currentIndex = tabs.indexOf(activeTab);
                    if (currentIndex < tabs.length - 1) {
                      setActiveTab(tabs[currentIndex + 1]);
                    }
                  }}
                  className="nav-btn-custom"
                >
                  {t("next")}
                </Button>
              )}
            </div>

            {/* Main Action Buttons */}
            <div className="d-flex gap-3">
              <Button
                variant="outline-secondary"
                onClick={onHide}
                disabled={isLoading}
                className="action-btn-secondary"
              >
                <Icon icon={FaTimes} className="me-2" />
                {t("cancel")}
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isLoading}
                className="action-btn-primary"
              >
                {isLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {t("saving")}...
                  </>
                ) : (
                  <>
                    <Icon icon={FaSave} className="me-2" />
                    {isEditing ? t("update") : t("save")}
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Form>

      <style>{`
        /* Modal Styles */
        .modern-modal .modal-content {
          border: none;
          border-radius: 20px;
          background: ${
            isDark ? "rgba(30, 41, 59, 0.95)" : "rgba(255, 255, 255, 0.95)"
          };
          backdrop-filter: blur(20px);
          box-shadow: ${
            isDark
              ? "0 25px 80px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)"
              : "0 25px 80px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)"
          };
        }
        
        .modal-header-custom {
          border-radius: 20px 20px 0 0;
          background: ${
            isDark ? "rgba(51, 65, 85, 0.5)" : "rgba(248, 250, 252, 0.8)"
          };
          backdrop-filter: blur(10px);
        }

        .modal-body-custom {
          background: transparent;
        }

        .modal-footer-custom {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.3)" : "rgba(248, 250, 252, 0.5)"
          };
          backdrop-filter: blur(10px);
          border-radius: 0 0 20px 20px;
        }

        .modal-icon-wrapper {
          width: 60px;
          height: 60px;
          background: ${
            isDark ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.1)"
          };
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid ${
            isDark ? "rgba(59, 130, 246, 0.3)" : "rgba(59, 130, 246, 0.2)"
          };
        }

        .modal-icon {
          color: #3b82f6;
        }

        .modal-title-custom {
          color: ${isDark ? "#f1f5f9" : "#111827"};
        }

        .modal-subtitle {
          color: ${isDark ? "#94a3b8" : "#6b7280"};
        }

        .close-btn-custom {
          border: 1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
          };
          background: ${
            isDark ? "rgba(51, 65, 85, 0.5)" : "rgba(255, 255, 255, 0.8)"
          };
          color: ${isDark ? "#cbd5e1" : "#6b7280"};
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .close-btn-custom:hover {
          background: ${
            isDark ? "rgba(239, 68, 68, 0.2)" : "rgba(239, 68, 68, 0.1)"
          };
          border-color: ${
            isDark ? "rgba(239, 68, 68, 0.3)" : "rgba(239, 68, 68, 0.2)"
          };
          color: #ef4444;
          transform: scale(1.05);
        }

        /* Error Alert */
        .error-alert-custom {
          background: ${
            isDark ? "rgba(239, 68, 68, 0.15)" : "rgba(254, 242, 242, 0.9)"
          };
          border: 1px solid ${
            isDark ? "rgba(239, 68, 68, 0.3)" : "rgba(254, 202, 202, 0.8)"
          };
          color: ${isDark ? "#fca5a5" : "#dc2626"};
          border-radius: 12px;
          backdrop-filter: blur(10px);
        }
        
        /* Form Controls */
        .form-control-modern {
          height: 48px;
          border-radius: 12px;
          border: 1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
          };
          background: ${
            isDark ? "rgba(51, 65, 85, 0.5)" : "rgba(248, 250, 252, 0.8)"
          };
          color: ${isDark ? "#f1f5f9" : "#111827"};
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .form-control-modern::placeholder {
          color: ${isDark ? "#64748b" : "#9ca3af"};
        }
        
        .form-control-modern:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25) !important;
          background: ${
            isDark ? "rgba(51, 65, 85, 0.8)" : "rgba(255, 255, 255, 0.9)"
          } !important;
        }
        
        .form-control-modern:hover {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.7)" : "rgba(243, 244, 246, 0.9)"
          } !important;
          border-color: ${
            isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.15)"
          };
        }

        /* Form Labels */
        .form-label-custom {
          font-weight: 600;
          color: ${isDark ? "#e2e8f0" : "#374151"};
          font-size: 14px;
        }

        /* Status Card */
        .status-card-custom {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.4)" : "rgba(248, 250, 252, 0.8)"
          };
          border: 1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
          };
          border-radius: 12px;
          padding: 20px;
          backdrop-filter: blur(10px);
        }

        .status-description {
          color: ${isDark ? "#94a3b8" : "#6b7280"};
          font-size: 13px;
        }

        /* Switch Custom */
        .switch-custom .form-check-input {
          background-color: ${
            isDark ? "rgba(71, 85, 105, 0.8)" : "rgba(209, 213, 219, 0.8)"
          };
          border-color: ${
            isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)"
          };
        }

        .switch-custom .form-check-input:checked {
          background-color: #10b981;
          border-color: #10b981;
        }

        /* Tab Styles */
        .nav-tabs-modern {
          border: none;
          background: ${
            isDark ? "rgba(51, 65, 85, 0.4)" : "rgba(248, 250, 252, 0.8)"
          };
          border-radius: 16px;
          padding: 8px;
          backdrop-filter: blur(10px);
          border: 1px solid ${
            isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)"
          };
        }

        .nav-tabs-modern .nav-link-custom {
          border: none;
          border-radius: 12px;
          padding: 16px 20px;
          margin: 0 4px;
          background: transparent;
          color: ${isDark ? "#94a3b8" : "#6b7280"};
          transition: all 0.3s ease;
          min-height: 80px;
          display: flex;
          align-items: center;
        }

        .nav-tabs-modern .nav-link-custom:hover {
          background: ${
            isDark ? "rgba(59, 130, 246, 0.15)" : "rgba(59, 130, 246, 0.1)"
          };
          color: #3b82f6;
          transform: translateY(-2px);
        }

        .nav-tabs-modern .nav-link-custom.active {
          background: ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.9)"
          };
          color: #3b82f6;
          box-shadow: ${
            isDark
              ? "0 4px 20px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)"
              : "0 4px 20px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)"
          };
          transform: translateY(-2px);
        }

        .tab-icon-wrapper {
          position: relative;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${
            isDark ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.1)"
          };
          border-radius: 10px;
          flex-shrink: 0;
          transition: all 0.3s ease;
        }

        .nav-tabs-modern .nav-link-custom.active .tab-icon-wrapper {
          background: rgba(59, 130, 246, 0.2);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
        }

        .tab-check-icon {
          position: absolute;
          top: -6px;
          right: -6px;
          background: #10b981;
          color: white;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
        }

        .tab-title {
          font-weight: 600;
          font-size: 15px;
          line-height: 1.3;
          margin-bottom: 2px;
        }

        .tab-subtitle {
          color: ${isDark ? "#64748b" : "#9ca3af"};
          font-size: 12px;
          line-height: 1.3;
        }

        .tab-content-wrapper {
          min-height: 320px;
          padding: 24px 0;
        }

        /* Navigation Buttons */
        .nav-btn-custom {
          border-radius: 10px;
          padding: 8px 16px;
          font-weight: 500;
          transition: all 0.3s ease;
          border: 1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
          };
          background: ${
            isDark ? "rgba(51, 65, 85, 0.5)" : "rgba(255, 255, 255, 0.8)"
          };
          backdrop-filter: blur(10px);
        }

        .nav-btn-custom:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        /* Action Buttons */
        .action-btn-secondary {
          border-radius: 12px;
          padding: 12px 24px;
          font-weight: 500;
          transition: all 0.3s ease;
          border: 1px solid ${
            isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.15)"
          };
          background: ${
            isDark ? "rgba(71, 85, 105, 0.5)" : "rgba(248, 250, 252, 0.8)"
          };
          color: ${isDark ? "#cbd5e1" : "#6b7280"};
          backdrop-filter: blur(10px);
        }

        .action-btn-secondary:hover {
          background: ${
            isDark ? "rgba(71, 85, 105, 0.7)" : "rgba(229, 231, 235, 0.9)"
          };
          border-color: ${
            isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.2)"
          };
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .action-btn-primary {
          border-radius: 12px;
          padding: 12px 24px;
          font-weight: 600;
          transition: all 0.3s ease;
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          border: none;
          color: white;
          box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
        }

        .action-btn-primary:hover {
          background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(59, 130, 246, 0.5);
        }

        .action-btn-primary:disabled {
          background: ${
            isDark ? "rgba(71, 85, 105, 0.5)" : "rgba(156, 163, 175, 0.5)"
          };
          box-shadow: none;
          transform: none;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .nav-tabs-modern {
            flex-direction: column;
            padding: 6px;
          }
          
          .nav-tabs-modern .nav-link-custom {
            margin: 2px 0;
            text-align: left;
            min-height: 70px;
            padding: 12px 16px;
          }

          .tab-icon-wrapper {
            width: 36px;
            height: 36px;
          }

          .tab-title {
            font-size: 14px;
          }

          .tab-subtitle {
            font-size: 11px;
          }

          .modal-icon-wrapper {
            width: 50px;
            height: 50px;
          }

          .action-btn-primary,
          .action-btn-secondary {
            padding: 10px 20px;
            font-size: 14px;
          }
        }

        /* Animation Effects */
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .modern-modal .modal-content {
          animation: modalSlideIn 0.3s ease-out;
        }

        /* Focus States */
        .form-control-modern:focus,
        .nav-link-custom:focus,
        .action-btn-primary:focus,
        .action-btn-secondary:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
        }

        /* Invalid Feedback */
        .invalid-feedback {
          color: #ef4444;
          font-size: 13px;
          margin-top: 6px;
        }

        .form-control.is-invalid {
          border-color: #ef4444;
          box-shadow: 0 0 0 0.2rem rgba(239, 68, 68, 0.25);
        }
      `}</style>
    </Modal>
  );
};

export default CompanyFormModal;
